import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/navbar/Header';
import Footer from '../../components/footer/footer';

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const privacyData = t('privacy.sections', { returnObjects: true }) as Record<string, any>;
  
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />
      
      <div className="container mx-auto px-4 py-12 md:py-20 max-w-4xl">
        <h1 className="text-3xl md:text-4xl font-bold mt-8 mb-8 text-gray-800 text-primary">
          {t('privacy.title')}
        </h1>

        <div className="prose prose-lg max-w-none text-gray-700">
          {/* Introduction */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.introduction.title}</h2>
            <p className="mb-4">{privacyData.introduction.content}</p>
          </section>

          {/* Responsable du Traitement */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.responsable.title}</h2>
            {privacyData.responsable.content.map((paragraph: string, i: number) => (
              <p key={i} className="mb-2">{paragraph}</p>
            ))}
          </section>

          {/* Données collectées */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.collectedData.title}</h2>
            
            <p className="mb-4">{privacyData.collectedData.intro}</p>
            
            {/* Clients */}
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-2">{privacyData.collectedData.clients.title}</h3>
              <ul className="list-disc pl-6 space-y-2">
                {privacyData.collectedData.clients.items.map((item: string, i: number) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ul>
            </div>
            
            {/* Professionnels */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-2">{privacyData.collectedData.professionals.title}</h3>
              <ul className="list-disc pl-6 space-y-2">
                {privacyData.collectedData.professionals.items.map((item: string, i: number) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ul>
            </div>
            
            {/* Note */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: privacyData.collectedData.note }} />
            </div>
          </section>

          {/* Finalités */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.purposes.title}</h2>
            
            <p className="mb-4">{privacyData.purposes.intro}</p>
            
            <ul className="list-disc pl-6 space-y-2">
              {privacyData.purposes.items.map((item: string, i: number) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: privacyData.purposes.note }} />
            </div>
          </section>

          {/* Partage des données */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.sharing.title}</h2>
            
            <p className="mb-4">{privacyData.sharing.intro}</p>
            
            <ul className="list-disc pl-6 space-y-2">
              {privacyData.sharing.items.map((item: string, i: number) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: privacyData.sharing.note }} />
            </div>
          </section>

          {/* Sécurité */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.security.title}</h2>
            {Array.isArray(privacyData.security.content) ? (
              <>
                {privacyData.security.content.map((paragraph: string, i: number) => (
                  <p key={i} className="mb-4">{paragraph}</p>
                ))}
                {privacyData.security.note && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{privacyData.security.note}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="mb-4">{privacyData.security.content}</p>
            )}
          </section>

          {/* Conservation */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.retention.title}</h2>
            
            <p className="mb-4">{privacyData.retention.intro}</p>
            
            <ul className="list-disc pl-6 space-y-2">
              {privacyData.retention.items.map((item: string, i: number) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: privacyData.retention.note }} />
            </div>
          </section>

          {/* Droits */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.rights.title}</h2>
            
            <p className="mb-4">{privacyData.rights.intro}</p>
            
            <ul className="list-disc pl-6 space-y-2">
              {privacyData.rights.items.map((item: string, i: number) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: privacyData.rights.contact + ' ' + privacyData.rights.complaint }} />
            </div>
          </section>

          {/* Cookies */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.cookies.title}</h2>
            {Array.isArray(privacyData.cookies.content) ? (
              <>
                {privacyData.cookies.content.map((paragraph: string, i: number) => (
                  <p key={i} className="mb-4">{paragraph}</p>
                ))}
                {privacyData.cookies.note && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{privacyData.cookies.note}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="mb-4">{privacyData.cookies.content}</p>
            )}
          </section>

          {/* Mises à jour */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.updates.title}</h2>
            {Array.isArray(privacyData.updates.content) ? (
              privacyData.updates.content.map((paragraph: string, i: number) => (
                <p key={i} className="mb-4">{paragraph}</p>
              ))
            ) : (
              <p className="mb-4">{privacyData.updates.content}</p>
            )}
          </section>

          {/* Mineurs */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.minors.title}</h2>
            {Array.isArray(privacyData.minors.content) ? (
              privacyData.minors.content.map((paragraph: string, i: number) => (
                <p key={i} className="mb-4">{paragraph}</p>
              ))
            ) : (
              <p className="mb-4">{privacyData.minors.content}</p>
            )}
          </section>

          {/* Transferts Internationaux */}
          {privacyData.international && (
            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.international.title}</h2>
              {Array.isArray(privacyData.international.content) ? (
                privacyData.international.content.map((paragraph: string, i: number) => (
                  <p key={i} className="mb-4">{paragraph}</p>
                ))
              ) : (
                <p className="mb-4">{privacyData.international.content}</p>
              )}
            </section>
          )}

          {/* Contact */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">{privacyData.contact.title}</h2>
            {Array.isArray(privacyData.contact.content) ? (
              privacyData.contact.content.map((paragraph: string, i: number) => (
                <p key={i} className="mb-2">{paragraph}</p>
              ))
            ) : (
              <p className="mb-4">{privacyData.contact.content}</p>
            )}
          </section>

          <div className="mt-12 text-sm text-gray-500">
            <p>{t('privacy.updatedAt')}</p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;
